/**
 * Formats a duration in seconds into a human-readable string
 * Shows minutes only if greater than 0, otherwise shows seconds only
 * @param seconds - The duration in seconds to format
 * @returns A formatted string in the format "Xs" or "Xm Ys"
 * @example
 * formatDuration(45) // returns "45s"
 * formatDuration(125) // returns "2m 5s"
 */
export const formatDuration = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  if (mins === 0) {
    return `${secs}s`;
  }

  return `${mins}m ${secs}s`;
};

/**
 * Formats the duration between two dates into a human-readable string
 * @param startDate - The start date or ISO string
 * @param endDate - The end date or ISO string (defaults to current time if not provided)
 * @returns A formatted string in the format "Xms", "Xs", or "Xm Ys"
 * @example
 * formatDurationFromDates("2024-01-01T10:00:00", "2024-01-01T10:02:05") // returns "2m 5s"
 * formatDurationFromDates("2024-01-01T10:00:00", "2024-01-01T10:00:05") // returns "5s"
 * formatDurationFromDates("2024-01-01T10:00:00", "2024-01-01T10:00:00.500") // returns "500ms"
 */
export const formatDurationFromDates = (
  startDate: string | Date,
  endDate?: string | Date
): string | null => {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;

  if (!start || Number.isNaN(start.getTime())) return null;

  const end = endDate
    ? typeof endDate === 'string'
      ? new Date(endDate)
      : endDate
    : new Date();

  if (Number.isNaN(end.getTime())) return null;

  const durationMs = end.getTime() - start.getTime();

  if (Math.abs(durationMs) < 1000) return `${Math.round(durationMs)}ms`;
  if (Math.abs(durationMs) < 60000) return `${Math.round(durationMs / 1000)}s`;

  const totalSeconds = Math.round(durationMs / 1000);
  return formatDuration(totalSeconds);
};
